import { createAsyncThunk } from '@reduxjs/toolkit';

import { setLoading } from 'src/components/common/loading/loadingSlice';
import {
	ILoginCredentials,
	ILoginResponse,
	IGetAccessTokenFromRefreshThunkArgs,
	IGetAccessTokenFromRefreshResponse,
} from 'src/features/auth/domain/types';
import { authApi } from 'src/features/auth/infrastructure/api';
import { handleThunkError } from 'src/utils/handleThunkError';

import { setAuthInfo } from './authSlice';

const AUTH_LOADING_KEY = 'auth';

export const login = createAsyncThunk<
	ILoginResponse,
	ILoginCredentials,
	{ rejectValue: IErrorResponse }
>('auth/login', async (credentials, { dispatch, rejectWithValue }) => {
	try {
		dispatch(setLoading({ key: AUTH_LOADING_KEY, loading: true }));
		const response = await authApi.login(credentials);

		localStorage.setItem('access_token', response.access);
		localStorage.setItem('refresh_token', response.refresh);

		return response;
	} catch (error: unknown) {
		const err = error as { response?: { data: IErrorResponse } };
		return rejectWithValue(
			err.response?.data || {
				description: 'Login failed',
				code: 0,
				reason: 'unknown_error',
			},
		);
	} finally {
		dispatch(setLoading({ key: AUTH_LOADING_KEY, loading: false }));
	}
});

export const getAccessTokenFromRefresh = createAsyncThunk<
	IGetAccessTokenFromRefreshResponse,
	IGetAccessTokenFromRefreshThunkArgs,
	{ rejectValue: IErrorResponse }
>(
	'auth/getAccessTokenFromRefresh',
	async (
		{ onSuccess, onError, refreshToken },
		{ dispatch, rejectWithValue },
	) => {
		try {
			dispatch(setLoading({ key: AUTH_LOADING_KEY, loading: true }));
			const response =
				await authApi.getAccessTokenFromRefresh(refreshToken);

			const storeTokens = async () =>
				dispatch(
					setAuthInfo({
						user_type: response.user_type,
						access: response.access,
						refresh: refreshToken,
					}),
				);

			await storeTokens();

			localStorage.setItem('access_token', response.access);
			localStorage.setItem('refresh_token', refreshToken);

			onSuccess?.(response);
			return response;
		} catch (error: unknown) {
			const err = handleThunkError(error, 'Failed to fetch access');
			onError?.(err);
			return rejectWithValue(err);
		} finally {
			dispatch(setLoading({ key: AUTH_LOADING_KEY, loading: false }));
		}
	},
);
